// Names: random guest names, username rules, and route names variants can't use as ids.

const ADJECTIVES = [
  'Sneaky', 'Brave', 'Wobbly', 'Sleepy', 'Turbo', 'Grumpy', 'Lucky', 'Sly', 'Bold', 'Clumsy',
  'Mighty', 'Swift', 'Silent', 'Fuzzy', 'Spicy', 'Chill', 'Wild', 'Cosmic', 'Tiny', 'Giant',
  'Salty', 'Zesty', 'Rapid', 'Jolly', 'Shady', 'Noble', 'Feral', 'Dapper', 'Frantic', 'Stoic',
];
const NOUNS = [
  'Pawn', 'Knight', 'Bishop', 'Rook', 'Queen', 'King', 'Snail', 'Castle', 'Gambit', 'Fork',
  'Pin', 'Tempo', 'Zugzwang', 'Blunder', 'Checkmate', 'Duck', 'Horsey', 'Sac', 'Endgame', 'Opening',
];

export function guestName(random: () => number = Math.random): string {
  const pick = <T>(list: readonly T[]) => list[Math.floor(random() * list.length)];
  return `${pick(ADJECTIVES)} ${pick(NOUNS)} ${10 + Math.floor(random() * 90)}`;
}

export const USERNAME_RULES = '3–20 letters, numbers, _ or -, starting with a letter or number';
const USERNAME_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{2,19}$/;
const RESERVED = new Set([
  'admin', 'administrator', 'root', 'system', 'support', 'help', 'mod', 'moderator', 'staff', 'official',
  'chessbut', 'techty', 'techtystudio', 'guest', 'anonymous', 'null', 'undefined', 'me', 'you', 'bot',
  'computer', 'stockfish', 'api', 'www', 'mail', 'team', 'owner',
]);
// Blocked words. Names are lowercased, leetspeak is undone (0→o, 1→i, 3→e, 4→a, 5→s, 7→t,
// @→a, $→s) and repeated letters collapsed before checking. Words in BLOCKED_ANYWHERE are
// caught inside longer names; BLOCKED_WORDS only as a separate word (so "grape", "raccoon",
// "Cassandra" and "Scunthorpe"-style names stay allowed). Anything that slips through can be
// renamed by an admin (cli.mjs rename).
const BLOCKED_ANYWHERE = [
  'fuck', 'fuk', 'fck', 'fvck', 'phuck', 'shit', 'cunt', 'nigg', 'nigr', 'niga', 'fagot', 'faggot', 'retard', 'whore', 'slut', 'nazi',
  'hitler', 'bitch', 'bastard', 'pussy', 'penis', 'vagina', 'porn', 'jizz', 'wank', 'dildo', 'molest', 'pedo',
  'rapist', 'kkk', 'heil', 'tranny', 'kike', 'chink', 'wetback', 'beaner', 'gook',
];
const BLOCKED_WORDS = ['ass', 'arse', 'cock', 'cum', 'rape', 'dick', 'tit', 'tits', 'sex', 'anal', 'twat', 'fag', 'spic', 'coon', 'paki', 'dyke', 'homo', 'nig', 'hoe', 'scat', 'anus', 'semen', 'boob', 'boobs', 'jew'];

const LEET: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '@': 'a', $: 's', '!': 'i', '|': 'l' };
const unleet = (s: string) => s.replace(/[013457@$!|]/g, (c) => LEET[c]);
const squash = (s: string) => s.replace(/(.)\1+/g, '$1');

/** True if the text contains a blocked word (usernames, guest names, ideas). */
export function isBlockedName(text: string): boolean {
  // Words: split camelCase and on anything that isn't a letter (before undoing leetspeak, so
  // "Sn4il" stays one word).
  const words = text
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9@$!|]+/)
    .map((w) => unleet(w).replace(/[^a-z]/g, ''))
    .filter(Boolean);
  const joined = words.join('');
  for (const form of [joined, squash(joined)]) if (BLOCKED_ANYWHERE.some((w) => form.includes(w))) return true;
  return words.some((w) => BLOCKED_WORDS.includes(w) || BLOCKED_WORDS.includes(squash(w)) || BLOCKED_WORDS.some((b) => w === `${b}s`));
}

/** A username to start from, made from a guest name ("Sleepy Duck 96" → "SleepyDuck96"). */
export function usernameFrom(name: string): string {
  let u = name.normalize('NFKD').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 20);
  if (!/^[A-Za-z0-9]/.test(u)) u = `p${u}`.slice(0, 20);
  return u.length >= 3 ? u : `${u}player`.slice(0, 20);
}

export function validateUsername(name: string): string | null {
  if (!USERNAME_RE.test(name)) return `Usernames are ${USERNAME_RULES}.`;
  const lower = name.toLowerCase();
  if (RESERVED.has(lower)) return 'That username is reserved.';
  if (isBlockedName(lower)) return 'Please pick a different username.';
  return null;
}

export const PASSWORD_MIN = 8;

export function validatePassword(pw: string): string | null {
  if (typeof pw !== 'string' || pw.length < PASSWORD_MIN) return `Passwords need at least ${PASSWORD_MIN} characters.`;
  if (pw.length > 200) return 'That password is too long.';
  return null;
}

/** Top-level paths the web app uses; variant ids must not collide with these. */
export const RESERVED_PATHS = new Set([
  'api', 'ws', 'assets', 'art', 'brand', 'og', 'day', 'game', 'join', 'u', 'leaderboard', 'stats',
  'admin', 'login', 'signup', 'settings', 'about', 'privacy', 'terms', 'rules', 'archive', 'today',
  'challenge', 'profile', 'logout', 'signin', 'ranks', 'ideas', 'd', 'days', 'you', 'favicon.ico', 'manifest.webmanifest', 'robots.txt', 'sitemap.xml',
]);
